import { LitElement, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { styles } from './safety-action-viewer.style.js';

import { SafetyActionStatus } from '@cmms/models';

@customElement('safety-action-viewer')
export class SafetyActionViewer extends LitElement {
  static styles = styles;

  @property({ type: Object }) action: any = null;
  @property({ type: Array }) users: any[] = [];
  @property({ type: Array }) safetyReports: any[] = [];

  connectedCallback() {
    super.connectedCallback();
  }

  private getStatusBadgeVariant(status: string) {
    switch (status) {
      case SafetyActionStatus.TODO:
        return 'neutral';
      case SafetyActionStatus.IN_PROGRESS:
        return 'primary';
      case SafetyActionStatus.COMPLETED:
        return 'success';
      default:
        return 'neutral';
    }
  }

  private getDueDateClass(dueDate: number) {
    const now = Date.now();
    const daysUntilDue = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntilDue < 0) return 'overdue';
    if (daysUntilDue <= 3) return 'due-soon';
    return '';
  }

  private formatDate(timestamp: number) {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  private getUserName(userId: string): string {
    if (!userId) return 'Unassigned';
    const user = this.users?.find(u => u.id === userId);
    return user ? (user.name || user.email || `User ${userId}`) : `User ${userId}`;
  }

  private getReportTitle(reportId: string): string {
    if (!reportId) return 'None';
    const report = this.safetyReports?.find(r => r.id === reportId);
    return report ? (report.title || report.report_title || report.incident_title || `Report ${reportId}`) : `Report ${reportId}`;
  }

  private closeModal() {
    const dialog = this.shadowRoot?.querySelector('sl-dialog') as any;
    dialog?.hide();
  }

  private editAction() {
    // Close viewer and open editor
    this.closeModal();
    const modal = document.createElement('safety-action-modal') as any;
    modal.action = this.action;
    document.body.appendChild(modal);
  }

  render() {
    if (!this.action) {
      return html`<sl-dialog label="Safety Action" @sl-hide="${() => this.remove()}"></sl-dialog>`;
    }

    return html`
      <sl-dialog
        label="Safety Action Details"
        @sl-hide="${() => this.remove()}"
      >
        <div class="action-header">
          <h2 class="action-title">${this.action.action_title}</h2>
          <div class="actions">
            <sl-button size="small" variant="text" @click="${this.editAction}">
              <sl-icon name="pencil"></sl-icon>
            </sl-button>
          </div>
        </div>

        <div class="action-meta">
          <sl-badge variant="${this.getStatusBadgeVariant(this.action.status)}" class="status-badge">
            ${this.action.status.replace('_', ' ').toUpperCase()}
          </sl-badge>
        </div>

        <div class="action-details">
          <div class="detail-group">
            <div class="detail-label">Assigned To</div>
            <div class="detail-value">${this.getUserName(this.action.assigned_to_user_id)}</div>
          </div>

          <div class="detail-group">
            <div class="detail-label">Due Date</div>
            <div class="detail-value">
              <span class="due-date ${this.getDueDateClass(this.action.due_date)}">
                ${this.formatDate(this.action.due_date)}
              </span>
            </div>
          </div>

          <div class="detail-group">
            <div class="detail-label">Related Report</div>
            <div class="detail-value">${this.getReportTitle(this.action.related_report_id)}</div>
          </div>

          <div class="detail-group">
            <div class="detail-label">Created</div>
            <div class="detail-value">${this.formatDate(this.action.created_at || Date.now())}</div>
          </div>
        </div>

        <div class="description-section">
          <div class="detail-label">Action Description</div>
          <div class="description-content">
            ${this.action.action_description}
          </div>
        </div>

        ${this.action.status === SafetyActionStatus.COMPLETED && this.action.completion_notes ? html`
          <div class="completion-section">
            <div class="detail-label">Completion Notes</div>
            <div class="completion-content">
              ${this.action.completion_notes}
            </div>
          </div>
        ` : ''}

        <div slot="footer" class="actions">
          <sl-button @click="${this.closeModal}">Close</sl-button>
        </div>
      </sl-dialog>
    `;
  }
}
